import { type ReactNode, useState } from 'react';
import { useUIStore, type NavView } from '../stores';
import { AuthModal } from './AuthModal';
import { ProfileMenu } from './ProfileMenu';
import { OfflineIndicator } from './OfflineIndicator';
import { UpdateBanner } from './UpdateBanner';
import './AppShell.css';

interface AppShellProps {
  children: ReactNode;
}

const NAV_ITEMS: { view: NavView; label: string; icon: string }[] = [
  { view: 'warmup', label: 'Warm-up', icon: '⚡' },
  { view: 'lanes', label: 'Lanes', icon: '◎' },
  { view: 'challenges', label: 'Challenges', icon: '🎯' },
  { view: 'tournaments', label: 'Tournaments', icon: '🏆' },
  { view: 'leaderboard', label: 'Leaderboard', icon: '📊' },
  { view: 'profile', label: 'Profile', icon: '◉' },
];

export function AppShell({ children }: AppShellProps) {
  const currentView = useUIStore((s) => s.currentView);
  const isLeftNavExpanded = useUIStore((s) => s.isLeftNavExpanded);
  const navigate = useUIStore((s) => s.navigate);
  const toggleLeftNav = useUIStore((s) => s.toggleLeftNav);
  const openModal = useUIStore((s) => s.openModal);
  
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);

  return (
    <div className="app-shell">
      {/* Update Banner (desktop only, shows above top bar) */}
      <UpdateBanner />

      {/* Top Bar */}
      <header className="top-bar">
        <div className="top-bar__left">
          <button 
            className="top-bar__menu-btn"
            onClick={toggleLeftNav}
            aria-label="Toggle navigation"
          >
            <span className="menu-icon" />
          </button>
          <div className="top-bar__logo">pyxl</div>
        </div>
        
        <nav className="top-bar__center">
          <button 
            className={`mode-btn ${currentView === 'warmup' ? 'mode-btn--active' : ''}`}
            onClick={() => navigate('warmup')}
          >
            Warm-up
          </button>
          <button 
            className={`mode-btn ${currentView === 'lanes' ? 'mode-btn--active' : ''}`}
            onClick={() => navigate('lanes')}
          >
            Practice
          </button>
          <button 
            className={`mode-btn ${currentView === 'challenges' ? 'mode-btn--active' : ''}`}
            onClick={() => navigate('challenges')}
          >
            Challenge
          </button>
        </nav>
        
        <div className="top-bar__right">
          <OfflineIndicator />
          <ProfileMenu onSignIn={() => setAuthModalOpen(true)} />
          <button
            className="icon-btn"
            onClick={() => openModal('settings')}
            aria-label="Settings"
          >
            ⚙
          </button>
        </div>
      </header>

      <div className="app-shell__body">
        {/* Left Nav */}
        <nav className={`left-nav ${isLeftNavExpanded ? 'left-nav--expanded' : 'left-nav--collapsed'}`}>
          <ul className="left-nav__list">
            {NAV_ITEMS.map((item) => (
              <li key={item.view}>
                <button
                  className={`nav-item ${currentView === item.view ? 'nav-item--active' : ''}`}
                  onClick={() => navigate(item.view)}
                  title={item.label}
                  aria-label={item.label}
                >
                  <span className="nav-item__icon">{item.icon}</span>
                  {isLeftNavExpanded && (
                    <span className="nav-item__label">{item.label}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Main Content */}
        <main className="main-content">
          {children}
        </main>
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
      />
    </div>
  );
}
